import { AxiosPromise } from 'axios';
import { instance } from '.';
import { URLS } from './index';
import { Logger } from 'fsts';
import { SearchParams } from '../model/searchParams';
import { DefaultBackendHelper } from '../utils/backendHelper';
import employee, { Employee, Value } from '../model/employee';
import ODataFilterBuilder from 'odata-filter-builder';
import { CONST } from '@/shared/utils/constants';
import { i18nGlobal } from '@/i18n';
import employeePasword, { EmployeePassword } from '../model/applicationUser';
import { AUTHORITIES } from '../store/modules/auth';

const logger = new Logger('backend.Employee');
export interface BackendEmployee {
  getEmployee(id: string): AxiosPromise<Employee>;
  getEmployees: (
    searchParams: SearchParams,
    locationIds?: string[],
    departmentId?: string,
    employedAfter?: boolean,
    alwaysIncludedEmployeesIds?: string[],
    includeTrainers?: boolean,
    roleIds?: string[],
    active?: boolean,
    notIncludedEmployeesIds?: string[]
  ) => AxiosPromise<Value>;
  getAllEmployees: () => AxiosPromise<Value>;
  getEmployeesAndTrainers: (searchParams: SearchParams) => AxiosPromise<Value>;
  getCountOfActiveAdminsManagers(): AxiosPromise<number>;
  deleteEmployee(id: string): AxiosPromise;
  updateEmployee(Employee: Employee): AxiosPromise<any>;
  updateAuthorities(id: string, authorities: string[]): AxiosPromise;
  setNewPasswordViaEmail(employeePassword: EmployeePassword): AxiosPromise;
}

export const defaultBackendEmployee: BackendEmployee = {
  getEmployee(id: string): AxiosPromise<Employee> {
    let url = `${URLS.employeeOdata}/${id}`;
    return instance.get<Employee>(url);
  },
  getEmployees(
    searchParams: SearchParams,
    locationIds?: string[],
    departmentId?: string,
    employedAfter?: boolean,
    alwaysIncludedEmployeesIds?: string[],
    includeTrainers?: boolean,
    roleIds?: string[],
    active?: boolean,
    notIncludedEmployeesIds?: string[]
  ): AxiosPromise<Value> {
    const odfb = ODataFilterBuilder('and');

    if (employedAfter != undefined) {
      const odfbEmployedAfter = ODataFilterBuilder('or');
      odfbEmployedAfter.eq(CONST.TillDate, null, false);
      odfbEmployedAfter.ge(CONST.TillDate, employedAfter, false);

      if (
        alwaysIncludedEmployeesIds !== undefined &&
        alwaysIncludedEmployeesIds.length
      ) {
        odfbEmployedAfter.in(CONST.Id, alwaysIncludedEmployeesIds, false);
      }

      odfb.and(odfbEmployedAfter);
    }

    if (!includeTrainers) {
      const odfbIncludeTrainers = ODataFilterBuilder('or');
      odfbIncludeTrainers.ne(CONST.AuthoritiesString, AUTHORITIES.TRAINER);

      if (
        alwaysIncludedEmployeesIds !== undefined &&
        alwaysIncludedEmployeesIds.length
      ) {
        odfbIncludeTrainers.in(CONST.Id, alwaysIncludedEmployeesIds, false);
      }

      odfb.and(odfbIncludeTrainers);
    }

    if (roleIds != undefined && roleIds.length) {
      const odfbRoleIds = ODataFilterBuilder('or');
      roleIds.forEach((x) =>
        odfbRoleIds.or(`Authorities/any(Authority: Authority eq '${x}')`)
      );

      if (
        alwaysIncludedEmployeesIds !== undefined &&
        alwaysIncludedEmployeesIds.length
      ) {
        odfbRoleIds.in(CONST.Id, alwaysIncludedEmployeesIds, false);
      }

      odfb.and(odfbRoleIds);
    }

    if (active !== undefined) {
      const odfbActive = ODataFilterBuilder('or');
      odfbActive.eq(CONST.Active, active, false);

      if (
        alwaysIncludedEmployeesIds !== undefined &&
        alwaysIncludedEmployeesIds.length
      ) {
        odfbActive.in(CONST.Id, alwaysIncludedEmployeesIds, false);
      }

      odfb.and(odfbActive);
    }

    if (
      notIncludedEmployeesIds != undefined &&
      notIncludedEmployeesIds.length
    ) {
      for (let notIcludedEmployeeId of notIncludedEmployeesIds) {
        odfb.ne(CONST.Id, notIcludedEmployeeId, false);
      }
    }

    const employeeIdsString: string = `[${
      alwaysIncludedEmployeesIds?.length
        ? alwaysIncludedEmployeesIds.map((id) => `'${id}'`).join(',')
        : ''
    }]`;
    const locationIdsString: string = `[${
      locationIds?.length ? locationIds.map((id) => `'${id}'`).join(',') : ''
    }]`;

    const baseUrl =
      locationIds != undefined
        ? `${URLS.employeeOdata}/GetInLocations(locationIds=${locationIdsString},alwaysIncludedEmployeesIds=${employeeIdsString})`
        : departmentId != undefined
        ? `${URLS.employeeOdata}/GetInDepartment(departmentId=${departmentId},alwaysIncludedEmployeesIds=${employeeIdsString})`
        : `${URLS.employeeOdata}`;

    let url = DefaultBackendHelper.makeUrl(
      baseUrl,
      searchParams.dataOption,
      searchParams.orClauseFieldsIds,
      searchParams.filter,
      odfb
    );

    url += '&$expand=Permissions';

    logger.log(`getEmployees${url}`);
    return instance.get<Value>(url);
  },

  getAllEmployees() {
    let url = DefaultBackendHelper.makeUrl(`${URLS.employee}/GetAll`);
    return instance.get<Value>(url);
  },

  getEmployeesAndTrainers(searchParams: SearchParams): AxiosPromise<Value> {
    var odfb = ODataFilterBuilder('and');

    let url = DefaultBackendHelper.makeUrl(
      `${URLS.employeeOdata}/GetEmployeesAndTrainers()`,
      searchParams.dataOption,
      searchParams.orClauseFieldsIds,
      searchParams.filter,
      odfb
    );
    logger.log(`GetEmployeesAndTrainers${url}`);
    return instance.get<Value>(url);
  },

  getCountOfActiveAdminsManagers(): AxiosPromise<number> {
    let url = `${URLS.employee}/GetCountOfActiveAdminsManagers`;
    return instance.get<number>(url);
  },

  deleteEmployee(id: string): AxiosPromise {
    logger.debug('deleteEmployee');
    return instance.delete(`${URLS.employee}/${id}`);
  },
  updateEmployee(Employee: Employee): AxiosPromise<any> {
    logger.debug('updateEmployee');

    if (Employee.id != '') {
      return instance.put<Employee>(
        `${URLS.employee}/${i18nGlobal.locale.value}`,
        employee.toAPI(Employee)
      );
    } else {
      return instance.post<Employee>(
        `${URLS.employee}/${i18nGlobal.locale.value}`,
        employee.toAPI(Employee)
      );
    }
  },
  updateAuthorities(id: string, authorities: string[]): AxiosPromise {
    return instance.put(`${URLS.employee}/UpdateAuthorities`, {
      ApplicationUserId: id,
      Authorities: authorities.join(','),
    });
  },
  setNewPasswordViaEmail(EmployeePassword: EmployeePassword): AxiosPromise {
    return instance.post(
      `${URLS.account}/setNewPasswordViaEmail`,
      employeePasword.toAPIEmployeePassword(EmployeePassword)
    );
  },
};
